import { defineStore } from 'pinia';
import CustomStore from 'devextreme/data/custom_store';
import type { LoadOptions } from 'devextreme/data';
import type {
  Breadcrumb,
  CustomStoreOptions,
  LoadOptionsParams,
  NestedDataGridFilterValues
} from '@/types';
import type { InputType } from '~/types';
import useApiService from '~/composables/api/useApiService';
import type { ApiError } from '~/types';
import { applyDateRangeFilter } from '~/mocks/data/commons/utils';

export const useStore = defineStore('store', {
  state: () => ({
    loading: false,
    dateRange: [] as Array<string>,
    loadArray: [] as Array<string>,
    isGlobalSearch: false,
    localeVersions: 0,
    externalFilterOutput: [] as NestedDataGridFilterValues,
    errorCode: undefined as number | undefined,
    password: { value: '', isValid: undefined } as InputType,
    pageBreadcrumb: undefined as Breadcrumb | undefined,
    loadmode: '' as string,
    exportTotalCount: 0 as number,
    totalCount: 0 as number
  }),
  actions: {
    setLoading(payload: boolean) {
      this.loading = payload;
    },
    setPageBreadcrumb(payload: Breadcrumb | undefined) {
      this.pageBreadcrumb = payload;
    },
    bumpLocaleVersions() {
      this.localeVersions += 1;
    },
    createDefaultStore(
      storeOptions: CustomStoreOptions & { onBeforeSend?: Function }
    ): CustomStore {
      this.loadmode = storeOptions.loadMode;
      return new CustomStore({
        key: storeOptions.keyExpr,
        load: async (loadOptions) => {
          const { dateRange, externalFilterOutput } = storeToRefs(useStore());

          if (storeOptions.onBeforeSend) {
            const mockRequest = {
              data: `skip=${loadOptions.skip || 0}&take=${loadOptions.take || 20}&sort=${JSON.stringify(loadOptions.sort || [])}`
            };
            storeOptions.onBeforeSend('load', mockRequest);
            const urlParams = new URLSearchParams(mockRequest.data);
            const sortParam = urlParams.get('sort');
            if (sortParam) {
              try {
                loadOptions.sort = JSON.parse(sortParam);
              } catch {
                loadOptions.sort = [{ selector: storeOptions.keyExpr, desc: true }];
              }
            }
          }

          const rawFilters = loadOptions.filter ?? [];

          let baseFilters: any[] = [...rawFilters];

          if (storeOptions.fixedFilter && storeOptions.fixedFilter.length > 0) {
            baseFilters =
              baseFilters.length > 0
                ? [...baseFilters, 'and', storeOptions.fixedFilter]
                : [storeOptions.fixedFilter];
          }

          if (storeOptions.hasExternalFilters && externalFilterOutput.value.length > 0) {
            const rawStr = JSON.stringify(rawFilters);

            const extractLeafFilters = (filters: any[]): string[] => {
              if (
                filters.length === 3 &&
                typeof filters[0] === 'string' &&
                typeof filters[1] === 'string'
              ) {
                return [JSON.stringify(filters)];
              }
              const leaves: string[] = [];
              for (const item of filters) {
                if (Array.isArray(item)) leaves.push(...extractLeafFilters(item));
              }
              return leaves;
            };

            const leafFilters = extractLeafFilters(externalFilterOutput.value);
            const externalAlreadyPresent =
              leafFilters.length > 0 && leafFilters.every((f) => rawStr.includes(f));

            if (rawFilters.length === 0) {
              baseFilters = [...externalFilterOutput.value];
            } else if (!externalAlreadyPresent) {
              baseFilters = [...rawFilters, 'and', ...externalFilterOutput.value];
            }
          }

          let composedFilter;

          if (storeOptions.dateFieldName) {
            composedFilter = applyDateRangeFilter(
              baseFilters,
              storeOptions.dateFieldName,
              dateRange.value,
              'DD-MM-YYYY'
            );
          } else if (storeOptions.hasExternalFilters && baseFilters.length > 0) {
            composedFilter = baseFilters;
          }

          const options: LoadOptionsParams = {
            skip: this.exportTotalCount > 0 ? 0 : (loadOptions.skip ?? 0),
            take: this.exportTotalCount > 0 ? this.exportTotalCount : (loadOptions.take ?? 20),
            requireTotalCount: false,
            paginateViaPrimaryKey: true,
            sort: JSON.stringify(
              loadOptions.sort ?? [{ selector: storeOptions.keyExpr, desc: true }]
            )
          };

          if (composedFilter) {
            options.filter = JSON.stringify(composedFilter);
          } else if (loadOptions.filter) {
            options.filter = JSON.stringify(loadOptions.filter);
          }

          const params = Object.entries(options)
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&');
          const { data: responseData, error: responseError } = await useApiService().get(
            `${storeOptions.loadUrl}?${params}`
          );
          if (responseError.value) {
            const cause = responseError.value.cause as ApiError;
            throw new Error(cause.Message);
          }
          const response = responseData.value;
          if (storeOptions.loadMode === 'processed')
            await this.loadDataGridCount(options, storeOptions.loadUrl);
          try {
            const result = response as any;
            if (!Array.isArray(result) && !result.data && typeof result === 'object') {
              return [result];
            }
            if (options.requireTotalCount && !Array.isArray(result)) {
              return { data: result.data };
            }
            if (storeOptions.loadMode === 'raw' && Array.isArray(result))
              this.totalCount = result.length;
            if (!Array.isArray(result) && result.data) {
              return { data: result.data, totalCount: this.totalCount };
            }
            return result;
          } catch (error) {
            console.error(error);
          }
        },
        loadMode: storeOptions.loadMode
      });
    },

    async loadDataGridCount(options: LoadOptions, loadUrl: string) {
      const optionsForCounts = { ...options, isCountQuery: true };
      const params = Object.entries(optionsForCounts as Record<string, any>)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&');
      const { data: countData, error: countError } = await useApiService().get(
        `${loadUrl}?${params}`
      );
      if (countError.value) {
        const cause = countError.value.cause as ApiError;
        throw new Error(cause.Message);
      }
      const countResponse = countData.value;
      this.totalCount = !Array.isArray(countResponse) ? (countResponse as any).totalCount : 0;
    }
  }
});
